import { useState } from 'react'
import type { BotType, Slot } from '../../types/game'
import { useGameStore } from '../../store/gameStore'
import { createWaitingRoom, transferMember, updateMemberAlias } from '../../api/rooms'
import { ApiError } from '../../api/client'
import { HostModal } from './HostModal'
import { JoinModal } from './JoinModal'
import { AdvancedSettingsModal } from './AdvancedSettingsModal'
import { defaultHostDraft, type HostDraft } from './lobbyDraft'
import { assetUrl } from '../../utils/assets'
import { WanderingBees } from './WanderingBees'

interface MenuScreenProps {
  shellMode: 'desktop' | 'mobile'
}

export function MenuScreen({ shellMode }: MenuScreenProps) {
  const {
    memberId,
    alias,
    setAlias,
    setRoomId,
    room,
    setError,
  } = useGameStore()

  const [localAlias, setLocalAlias] = useState(alias)
  const [lobbyView, setLobbyView] = useState<'menu' | 'host' | 'join' | 'advanced-settings'>('menu')
  const [hostDraft, setHostDraft] = useState<HostDraft>(defaultHostDraft(alias))
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const [isJoining, setIsJoining] = useState(false)

  const handleAliasBlur = async () => {
    const trimmed = localAlias.trim()
    if (trimmed && trimmed !== alias) {
      setAlias(trimmed)
      setHostDraft((current) => {
        const previousDefault = defaultHostDraft(alias).roomName
        return {
          ...current,
          roomName: current.roomName === previousDefault ? defaultHostDraft(trimmed).roomName : current.roomName,
        }
      })
      if (room) {
        try {
          await updateMemberAlias(room.id, memberId, trimmed)
        } catch {}
      }
    }
  }

  const handleHost = async (opts: {
    name: string
    donationMode: HostDraft['donationMode']
    eggIncubation: HostDraft['eggIncubation']
    incrementalBroodChamberCost: boolean
    infectionSpread: boolean
    survivorFavorMode: HostDraft['survivorFavorMode']
    winCondition: HostDraft['winCondition']
    publicOffers: boolean
    unlimitedQueenTrade: boolean
    slotStates: Record<string, Slot>
    botTypes: Record<string, BotType>
  }) => {
    setIsJoining(true)
    setError(null)
    try {
      const result = await createWaitingRoom({
        host_id: memberId,
        host_alias: localAlias.trim() || alias,
        name: opts.name,
        is_visible_in_lobby: true,
        slot_states: opts.slotStates,
        bot_types: opts.botTypes,
        donation_mode: opts.donationMode,
        egg_incubation: opts.eggIncubation,
        incremental_brood_chamber_cost: opts.incrementalBroodChamberCost,
        infection_spread: opts.infectionSpread,
        survivor_favor_mode: opts.survivorFavorMode,
        win_condition: opts.winCondition,
        public_offers: opts.publicOffers,
        unlimited_queen_trade: opts.unlimitedQueenTrade,
      })
      setRoomId(result.room_id)
      setLobbyView('menu')
    } catch (err) {
      if (err instanceof ApiError) setError(err.message)
      else setError('Failed to create room')
    } finally {
      setIsJoining(false)
    }
  }

  const handleJoin = async () => {
    if (!selectedRoomId || !room?.id) {
      return
    }

    setIsJoining(true)
    setError(null)
    try {
      const result = await transferMember({
        member_id: memberId,
        source_room_id: room.id,
        target_room_id: selectedRoomId,
      })
      setRoomId(result.room_id)
      setLobbyView('menu')
      setSelectedRoomId(null)
    } catch (err) {
      if (err instanceof ApiError) setError(err.message)
      else setError('Failed to join room')
    } finally {
      setIsJoining(false)
    }
  }

  const openHost = () => {
    setHostDraft(defaultHostDraft(localAlias.trim() || alias))
    setLobbyView('host')
  }

  const openJoin = () => {
    setSelectedRoomId(null)
    setLobbyView('join')
  }

  if (lobbyView === 'host') {
    return (
      <HostModal
        alias={localAlias.trim() || alias}
        draft={hostDraft}
        onChange={setHostDraft}
        onBack={() => setLobbyView('menu')}
        onAdvanced={() => setLobbyView('advanced-settings')}
        onHost={handleHost}
      />
    )
  }

  if (lobbyView === 'advanced-settings') {
    return (
      <AdvancedSettingsModal
        draft={hostDraft}
        onChange={setHostDraft}
        onBack={() => setLobbyView('host')}
      />
    )
  }

  if (lobbyView === 'join') {
    return (
      <JoinModal
        selectedRoomId={selectedRoomId}
        onSelect={setSelectedRoomId}
        onJoin={handleJoin}
        onBack={() => {
          setSelectedRoomId(null)
          setLobbyView('menu')
        }}
      />
    )
  }

  if (shellMode === 'mobile') {
    return (
      <>
        <div className="lobby-overlay" />
        <WanderingBees />
        <div className="menu-mobile-modal-container">
          <div className="lobby tableau menu-screen-mobile mobile-modal-shell">
            <div className="menu-mobile-shell-content">
              <div className="menu-mobile-layout">
                <div className="menu-mobile-hero">
                  <div className="menu-image-frame">
                    <img
                      src={assetUrl('images', 'colony_collapse.png')}
                      alt="Colony Collapse"
                      className="menu-hero-image"
                    />
                  </div>
                </div>
                <div className="menu-mobile-side">
                  <div className="mobile-modal-section menu-mobile-intro">
                    <div className="mobile-modal-title">Colony Collapse</div>
                  </div>
                  <div className="mobile-modal-section menu-name-slot">
                    <div className="mobile-modal-section-title">Username</div>
                    <input
                      type="text"
                      className="input menu-name-input"
                      placeholder="Enter your name..."
                      value={localAlias}
                      onChange={(event) => setLocalAlias(event.target.value)}
                      onBlur={handleAliasBlur}
                      onKeyDown={(event) => event.key === 'Enter' && void handleAliasBlur()}
                      maxLength={24}
                    />
                  </div>
                  <div className="mobile-modal-section menu-mobile-actions">
                    <div id="menu-mobile-host-button" className="lobby-start" onClick={isJoining ? undefined : openHost}>
                      Host
                    </div>
                    <div id="menu-mobile-join-button" className="lobby-start" onClick={isJoining ? undefined : openJoin}>
                      Join
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="lobby-overlay" />
      <WanderingBees />
      <div className="menu-modal-container">
        <div className="menu-status-container">
          <div className="menu-status">
            <div className="menu-status-content">Colony Collapse</div>
          </div>
        </div>

        <div className="lobby tableau menu-screen">
          <div className="menu-image-frame">
            <img
              src={assetUrl('images', 'colony_collapse.png')}
              alt="Colony Collapse"
              className="menu-hero-image"
            />
          </div>

          <div className="menu-body">
            <div className="lobby-slot menu-name-slot">
              <div className="lobby-member-header">Username</div>
              <input
                type="text"
                className="input menu-name-input"
                placeholder="Enter your name..."
                value={localAlias}
                onChange={(event) => setLocalAlias(event.target.value)}
                onBlur={handleAliasBlur}
                onKeyDown={(event) => event.key === 'Enter' && handleAliasBlur()}
                maxLength={24}
              />
            </div>

            <div className="lobby-start-container menu-start-container">
              <div id="menu-host-button" className="lobby-start" onClick={isJoining ? undefined : openHost}>
                Host
              </div>
              <div id="menu-join-button" className="lobby-start" onClick={isJoining ? undefined : openJoin}>
                Join
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
